import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import DocumentConfirmation from '../shared/DocumentConfirmation';

interface OnboardingStep {
  label: string;
  completed: boolean;
  path: string;
  icon: React.ReactNode;
}

interface ReferenceStatus {
  allSubmitted: boolean;
  totalCount: number;
  submittedCount: number;
}

interface DocumentStatus {
  allConfirmed: boolean;
  confirmedCount: number;
  requiredCount: number;
}

const CounsellorOnboardingDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [counsellorId, setCounsellorId] = useState<string | null>(null);
  const [referenceStatus, setReferenceStatus] = useState<ReferenceStatus>({
    allSubmitted: false,
    totalCount: 0,
    submittedCount: 0
  });
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus>({
    allConfirmed: false,
    confirmedCount: 0,
    requiredCount: 0
  });
  const [legalFormSubmitted, setLegalFormSubmitted] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [counsellorName, setCounsellorName] = useState('');

  const fetchCounsellorData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    // Always proceed with rendering the dashboard, even if we can't get a counsellorId
    // This ensures the page loads even if API endpoints fail
    let counsellorId = user?.counsellorId;
    let foundCounsellorId = false;
    
    // Try multiple methods to get counsellorId, but don't block rendering if they all fail
    if (!counsellorId) {
      try {
        const userResponse = await api.get(`/api/users/${user?._id}`);
        counsellorId = userResponse.data.counsellorId;
        if (counsellorId) foundCounsellorId = true;
      } catch (userError: any) {
        console.error('Error fetching user data:', userError);
      }
    } else {
      foundCounsellorId = true;
    }
    
    // If still not found, try the dedicated endpoint as a last resort
    if (!foundCounsellorId) {
      try {
        const counsellorResponse = await api.get(`/api/users/${user?._id}/counsellor`);
        counsellorId = counsellorResponse.data.counsellorId;
        if (counsellorId) foundCounsellorId = true;
      } catch (counsellorError: any) {
        console.error('Error fetching counsellor by user ID:', counsellorError);
      }
    }
    
    // If we found a counsellorId, use it, otherwise generate a placeholder
    if (counsellorId) {
      setCounsellorId(counsellorId);
    } else {
      // Use user ID as a fallback to allow the page to render
      // This won't work for API calls but prevents the UI from breaking
      setCounsellorId(user?._id || 'unknown');
      console.warn('Using fallback ID for counsellor - API calls may fail');
    }
    
    // Fetch reference status - use Promise.allSettled to handle all API calls in parallel
    // and ensure we continue even if some fail
    const apiPromises = [];
    
    // Only make API calls if we have a real counsellorId
    if (foundCounsellorId) {
      // Reference status API call
      apiPromises.push(
        api.get(`/api/referee/counsellor/${counsellorId}/references/status`)
          .then(response => {
            console.log('Reference status API response:', response.data);
            console.log('Setting reference status - submitted:', response.data.submittedCount, 'total:', response.data.totalCount);
            setReferenceStatus({
              allSubmitted: response.data.allSubmitted,
              totalCount: response.data.totalCount,
              submittedCount: response.data.submittedCount
            });
            return { success: true, type: 'references' };
          })
          .catch(error => {
            console.error('Error fetching reference status:', error);
            // Fallback to fetching counsellor data directly to check references
            return api.get(`/api/counsellors/${counsellorId}`)
              .then(response => {
                const counsellorData = response.data;
                const firstReferee = counsellorData.application?.referee?.firstReferee;
                const secondReferee = counsellorData.application?.referee?.secondReferee;
                
                const totalCount = 2; // We always expect 2 references
                let submittedCount = 0;
                
                // Check if references have been submitted - only count if referenceContent has actual data
                // A reference is only considered complete when the referee has actually submitted the reference form
                // Do NOT count references as submitted just because they were sent out (referenceEmailSent)
                if (firstReferee && 
                    firstReferee.referenceContent &&
                    typeof firstReferee.referenceContent === 'object' &&
                    Object.keys(firstReferee.referenceContent).length > 0 &&
                    firstReferee.referenceContent.howLongKnown) {
                  submittedCount++;
                }
                if (secondReferee && 
                    secondReferee.referenceContent &&
                    typeof secondReferee.referenceContent === 'object' &&
                    Object.keys(secondReferee.referenceContent).length > 0 &&
                    secondReferee.referenceContent.howLongKnown) {
                  submittedCount++;
                }
                
                const allSubmitted = submittedCount === totalCount;
                
                setReferenceStatus({
                  allSubmitted,
                  totalCount,
                  submittedCount
                });
                
                return { success: true, type: 'references' };
              })
              .catch(err => {
                console.error('Error fetching counsellor data for references:', err);
                setReferenceStatus({
                  allSubmitted: false,
                  totalCount: 2,
                  submittedCount: 0
                });
                return { success: false, type: 'references', error: err };
              });
          })
      );
      
      // Document status API call
      apiPromises.push(
        api.get(`/api/counsellors/${counsellorId}/documents/check-required`)
          .then(response => {
            setDocumentStatus({
              allConfirmed: response.data.allConfirmed,
              confirmedCount: response.data.confirmedCount,
              requiredCount: response.data.requiredCount
            });
            return { success: true, type: 'documents' };
          })
          .catch(error => {
            console.error('Error fetching document status:', error);
            setDocumentStatus({
              allConfirmed: false,
              confirmedCount: 0,
              requiredCount: 3 // Default assumption
            });
            return { success: false, type: 'documents', error };
          })
      );
      
      // Counsellor data API call
      apiPromises.push(
        api.get(`/api/counsellors/${counsellorId}`)
          .then(response => {
            setLegalFormSubmitted(!!response.data.legalFormSubmitted);
            setOnboardingCompleted(!!response.data.onboardingCompleted);
            
            // Set counsellor name for welcome message
            if (response.data.firstName) {
              setCounsellorName(response.data.firstName);
            }
            
            // If counsellor's applicationStatus is 'Active', redirect to dashboard
            if (response.data.applicationStatus === 'Active') {
              navigate('/counsellor/dashboard');
            }
            return { success: true, type: 'counsellor' };
          })
          .catch(error => {
            console.error('Error fetching counsellor data:', error);
            setLegalFormSubmitted(false);
            setOnboardingCompleted(false);
            return { success: false, type: 'counsellor', error };
          })
      );
    } else {
      // Set default values if we don't have a valid counsellorId
      setReferenceStatus({
        allSubmitted: false,
        totalCount: 2,
        submittedCount: 0
      });
      
      setDocumentStatus({
        allConfirmed: false,
        confirmedCount: 0,
        requiredCount: 3
      });
      
      setLegalFormSubmitted(false);
      setOnboardingCompleted(false);
    }
    
    // Wait for all API calls to complete (success or failure)
    if (apiPromises.length > 0) {
      const results = await Promise.all(apiPromises);
      const failedCalls = results.filter(result => !result.success);
      
      // Set a user-friendly error message if any API calls failed
      if (failedCalls.length > 0) {
        setError('Some onboarding information could not be loaded. You can still use the dashboard, but some data may be incomplete.');
      }
    } else if (!foundCounsellorId) {
      // Only show this error if we couldn't find a counsellorId at all
      setError('Your counsellor profile is still being set up. Some features may be limited until setup is complete.');
    }
    
    setLoading(false);
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      fetchCounsellorData();
    }
  }, [user, fetchCounsellorData]);

  const getOnboardingSteps = (): OnboardingStep[] => {
    return [
      {
        label: 'References',
        completed: referenceStatus.allSubmitted,
        path: counsellorId ? `/counsellor/${counsellorId}/references` : '',
        icon: <PersonIcon />
      },
      {
        label: 'Document Confirmations',
        completed: documentStatus.allConfirmed,
        path: '#documents', // Anchor to the documents section on this page
        icon: <DescriptionIcon />
      },
      {
        label: 'Legal Information',
        completed: legalFormSubmitted,
        path: counsellorId ? `/counsellor/${counsellorId}/legal` : '',
        icon: <GavelIcon />
      }
    ];
  };

  const getCompletionPercentage = (): number => {
    const steps = getOnboardingSteps();
    const completedSteps = steps.filter(step => step.completed).length;
    return Math.round((completedSteps / steps.length) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const steps = getOnboardingSteps();
  const completionPercentage = getCompletionPercentage();
  const allStepsCompleted = steps.every(step => step.completed);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            Counsellor Onboarding
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LogoutIcon />}
            onClick={logout}
            sx={{ minWidth: 'auto' }}
          >
            Logout
          </Button>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            {counsellorName ? `Welcome ${counsellorName} to your onboarding dashboard.` : 'Welcome to your onboarding dashboard.'} Please complete all the required steps below to finish your onboarding process.
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Completion: {completionPercentage}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={completionPercentage} 
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>
        </Box>
        
        {allStepsCompleted && !onboardingCompleted && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              You have completed all onboarding steps! Your profile is now under review by our administrators.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Once approved, you will be able to access all counsellor features.
            </Typography>
          </Box>
        )}
        
        {onboardingCompleted && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your onboarding has been approved! You now have full access to all counsellor features.
            </Alert>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              Go to Counsellor Dashboard
            </Button>
          </Box>
        )}
      </Paper>
      
      <Grid container spacing={3}>
        {/* References Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">References</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {referenceStatus.submittedCount === referenceStatus.totalCount && referenceStatus.totalCount > 0 ? (
                    <>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        Completed
                      </Typography>
                    </>
                  ) : (
                    <>
                      <AccessTimeIcon color="warning" fontSize="small" />
                      <Typography variant="body2" color="warning.main" fontWeight="medium">
                        Pending
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Status: <strong>{referenceStatus.submittedCount}</strong> of <strong>{referenceStatus.totalCount}</strong> references submitted
                  {referenceStatus.submittedCount > 0 && referenceStatus.submittedCount < referenceStatus.totalCount && (
                    <span> (Partially Complete)</span>
                  )}
                  {referenceStatus.submittedCount === 0 && (
                    <span> (Incomplete)</span>
                  )}
                  {referenceStatus.submittedCount === referenceStatus.totalCount && referenceStatus.totalCount > 0 && (
                    <span> (Complete)</span>
                  )}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(referenceStatus.submittedCount / Math.max(1, referenceStatus.totalCount)) * 100} 
                  color={referenceStatus.submittedCount === referenceStatus.totalCount && referenceStatus.totalCount > 0 ? "primary" : "warning"}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Button 
                variant="outlined" 
                onClick={() => {
                  if (counsellorId) {
                    navigate(`/counsellor/${counsellorId}/references`);
                  } else {
                    setError('Unable to access references. Please try again later.');
                  }
                }}
                fullWidth
              >
                Manage References
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Legal Information Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GavelIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Legal Information</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {legalFormSubmitted ? (
                    <>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        Completed
                      </Typography>
                    </>
                  ) : (
                    <>
                      <AccessTimeIcon color="warning" fontSize="small" />
                      <Typography variant="body2" color="warning.main" fontWeight="medium">
                        Pending
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Status: {legalFormSubmitted ? 'Submitted' : 'Incomplete'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {legalFormSubmitted ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : (
                    <CancelIcon color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="body2">
                    {legalFormSubmitted 
                      ? 'All legal information has been provided.' 
                      : 'Please complete your legal information.'}
                  </Typography>
                </Box>
              </Box>
              
              <Button 
                variant="outlined" 
                onClick={() => {
                  if (counsellorId) {
                    navigate(`/counsellor/${counsellorId}/legal`);
                  } else {
                    setError('Unable to access legal information. Please try again later.');
                  }
                }}
                fullWidth
              >
                {legalFormSubmitted ? 'View Legal Information' : 'Complete Legal Information'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Document Confirmations Section */}
      <Paper sx={{ p: 3, mt: 4 }} id="documents">
        
        {counsellorId && (
          <DocumentConfirmation 
            counsellorId={counsellorId}
            onSuccess={(message) => {
              setSuccess(message);
              fetchCounsellorData(); // Refresh data after successful confirmation
            }}
            onError={(message) => setError(message)}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CounsellorOnboardingDashboard;
